/**
 * ROUTE TREE CONFIGURATION
 *
 * Defines the application's routing structure using TanStack Router.
 * This configuration maps URL paths to React components and establishes
 * the navigation hierarchy for the Home Language Survey application.
 *
 * Route Structure:
 * - / : Main survey form (public access)
 * - /process : Survey management interface (authenticated)
 * - /studentsearch : Student search functionality (authenticated)
 * - /languagetable : Language reference tables (authenticated)
 * - /form/:id : Individual survey comparison view (authenticated)
 * - /student/:id : Student profile and data management (authenticated)
 *
 * The Layout component serves as the root wrapper, providing common
 * navigation, authentication, and styling for all routes.
 */

import { createRootRoute, createRoute } from '@tanstack/react-router'
import Layout from './Layout'
// Import all page components
import Survey from './component/survey/Survey'
import ProcessSurveys from './component/process/ProcessSurveys'
import ProcessCrossReference from './component/compare/ProcessCrossReference'
import StudentAndEdFusion from './component/student/Forms/StudentAndEdFusion'
import UpdateStudent from './component/compare/UpdateStudent'
import Page_404 from './Page_404'
import StudentSearch from './component/student/Search/StudentSearch'
import LanguageTable from './component/isolanguagetable/LanguageTable'

/**
 * ROOT ROUTE
 *
 * Base route that wraps all application routes with the Layout component.
 * Provides consistent navigation, authentication, and styling across the app.
 */
const rootRoute = createRootRoute({
  component: Layout,
  notFoundComponent: Page_404,
})

/**
 * HOME/SURVEY ROUTE (/)
 *
 * Main entry point for the application. Displays the home language survey form
 * where families can submit language information about their students.
 * Public access - no authentication required for survey submission.
 */
const surveyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: Survey,
})

/**
 * PROCESS SURVEYS ROUTE (/process)
 *
 * Administrative interface for viewing and managing submitted surveys.
 * Requires authentication - used by school staff to review survey data
 * and identify potential English Learners.
 */
const processRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/process',
  component: ProcessSurveys,
})

/**
 * STUDENT SEARCH ROUTE (/studentsearch)
 *
 * Advanced search interface for finding students in the state system.
 * Authentication required - used by administrators for student lookup
 * and demographic analysis.
 */
const studentSearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/studentsearch',
  component: StudentSearch,
})

/**
 * LANGUAGE TABLE ROUTE (/languagetable)
 *
 * Reference interface for viewing and managing language codes and mappings.
 * Authentication required - used for maintaining language data integrity
 * and ISO 639-3 compliance.
 */
const languageTableRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/languagetable',
  component: LanguageTable,
})

/**
 * FORM COMPARISON ROUTE (/form/:id)
 *
 * Detailed view for comparing survey responses and making updates.
 * Dynamic route with survey ID parameter. Authentication required.
 * Used for side-by-side comparison and data verification.
 */
const formRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/form/$id',
  component: ProcessCrossReference,
})
const updateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/survey/$id',
  component: UpdateStudent,
})
/**
 * STUDENT PROFILE ROUTE (/student/:id)
 *
 * Individual student profile and data management interface.
 * Dynamic route with student ID parameter. Authentication required.
 * Used for viewing and editing student demographic information.
 */
const studentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/student/$id',
  component: StudentAndEdFusion,
})

const notFoundRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/*',
  component: Page_404,
})

export const routeTree = rootRoute.addChildren([
  surveyRoute,
  processRoute,
  studentSearchRoute,
  languageTableRoute,
  formRoute,
  updateRoute,
  studentRoute,
  notFoundRoute,
])
